/**
 * Mojaloop switch deployment reference — the content catalog behind the
 * Switch Deployment page.
 *
 * MAINTENANCE: this file is the single source of truth for that page. All
 * version numbers, links and sizing figures live HERE, not in JSX. When a new
 * Mojaloop release lands, re-verify each section against the linked sources
 * and bump `verified` below. docs.mojaloop.io remains the authoritative
 * source — this page is the CoE's curated map into it.
 */

export const VERIFIED = {
  mojaloopVersion: 'v17.2.0',
  helmChartRelease: 'mojaloop/helm v17.2.0 (Jun 2026)',
  isoSince: 'v17.0.0 “Irrawaddy” (Mar 2025)',
  verifiedOn: '2026-07-23',
  authoritative: 'https://docs.mojaloop.io',
};

export const KEY_LINKS = [
  { name: 'Mojaloop documentation (authoritative)', url: 'https://docs.mojaloop.io' },
  { name: 'Deployment guide', url: 'https://docs.mojaloop.io/technical/deployment-guide/' },
  { name: 'Installing Mojaloop', url: 'https://docs.mojaloop.io/getting-started/installation/installing-mojaloop.html' },
  { name: 'Deployment tools (IaC)', url: 'https://docs.mojaloop.io/product/features/deployment/tools.html' },
  { name: 'ISO 20022 market practice', url: 'https://docs.mojaloop.io/product/features/iso20022.html' },
  { name: 'Helm charts repository', url: 'https://github.com/mojaloop/helm' },
  { name: 'Helm releases & changelogs', url: 'https://github.com/mojaloop/helm/releases' },
  { name: 'Infrastructure-as-Code modules', url: 'https://github.com/mojaloop/iac-modules' },
  { name: 'mini-loop (lab installer)', url: 'https://github.com/mojaloop/mini-loop' },
  { name: 'Testing Toolkit (TTK)', url: 'https://github.com/mojaloop/ml-testing-toolkit' },
  { name: 'Mojaloop community', url: 'https://community.mojaloop.io' },
  { name: 'Roadmap', url: 'https://docs.mojaloop.io/community/mojaloop-roadmap.html' },
];

/** Core hub services a DFSP's traffic actually touches, in flow order. */
export const CORE_SERVICES = [
  {
    name: 'account-lookup-service',
    repo: 'https://github.com/mojaloop/account-lookup-service',
    phase: 'Discovery',
    role: 'Resolves who holds an account: party lookup against pluggable oracles. '
        + 'The production counterpart of the sandbox account-lookup exchange.',
  },
  {
    name: 'quoting-service',
    repo: 'https://github.com/mojaloop/quoting-service',
    phase: 'Agreement',
    role: 'Agreement of terms: quotes, fees and the cryptographic condition the '
        + 'transfer must later fulfil.',
  },
  {
    name: 'ml-api-adapter',
    repo: 'https://github.com/mojaloop/ml-api-adapter',
    phase: 'Transfer edge',
    role: 'The DFSP-facing transfer API edge — accepts FSPIOP and (since v17) '
        + 'ISO 20022 messages, and delivers the hub’s async callbacks to your endpoints.',
  },
  {
    name: 'central-ledger',
    repo: 'https://github.com/mojaloop/central-ledger',
    phase: 'Clearing',
    role: 'The clearing ledger: participant positions, liquidity checks, and '
        + 'transfer prepare/fulfil against net debit caps.',
  },
  {
    name: 'central-settlement',
    repo: 'https://github.com/mojaloop/central-settlement',
    phase: 'Settlement',
    role: 'Settlement windows and net settlement between participating DFSPs — '
        + 'the DFSP↔DFSP funds movement that certification deliberately leaves to the scheme.',
  },
  {
    name: 'transaction-requests-service',
    repo: 'https://github.com/mojaloop/transaction-requests-service',
    phase: 'Request-to-Pay',
    role: 'Payee-initiated flows (Request-to-Pay) — the production counterpart of '
        + 'the sandbox rtp-request callback.',
  },
  {
    name: 'central-event-processor',
    repo: 'https://github.com/mojaloop/central-event-processor',
    phase: 'Notifications',
    role: 'Rules and notification processing over the event stream (limits, alerts).',
  },
  {
    name: 'ml-testing-toolkit',
    repo: 'https://github.com/mojaloop/ml-testing-toolkit',
    phase: 'Validation',
    role: 'Simulates DFSPs and runs the Golden Path conformance collections — the '
        + 'hub-side analogue of this sandbox’s certification suite.',
  },
];

export const SUPPORTING_STACK = [
  { name: 'Apache Kafka', role: 'Event backbone between hub services — size and operate for HA first.' },
  { name: 'MySQL / Percona', role: 'Ledger and service databases; HA + tested backup/restore are non-negotiable in production.' },
  { name: 'Redis', role: 'Caching for lookup/quoting paths.' },
  { name: 'MongoDB', role: 'Object store for the Testing Toolkit and reporting components.' },
  { name: 'Ingress / service mesh', role: 'TLS termination and routing for DFSP-facing APIs (nginx or Istio, per deployment choice).' },
  { name: 'HashiCorp Vault', role: 'Secrets and PKI (bundled by the IaC path).' },
  { name: 'Keycloak', role: 'Identity and access for operator portals (bundled by the IaC path).' },
  { name: 'Prometheus + Grafana, EFK', role: 'Metrics dashboards and centralised logging — deploy the monitoring chart alongside the hub.' },
];

/** The three deployment paths, by intent. */
export const DEPLOYMENT_PATHS = [
  {
    key: 'lab',
    title: 'Lab & evaluation — mini-loop',
    audience: 'Anyone exploring the switch',
    tool: 'mini-loop (k3s on a single VM)',
    repo: 'https://github.com/mojaloop/mini-loop',
    docs: 'https://docs.mojaloop.io/getting-started/installation/installing-mojaloop.html',
    summary:
      'Deploys a full Mojaloop hub on one Linux VM (Ubuntu 22, x86_64) with a couple of '
      + 'commands, in roughly 30 minutes. Ideal for learning the moving parts, demos, and '
      + 'developer experiments. Never a production shape.',
    steps: [
      'Provision one VM: Ubuntu 22 x86_64, 8 GB RAM minimum (16 GB is far more comfortable), 60+ GB disk',
      'Clone mojaloop/mini-loop and run the install scripts (installs k3s + the Mojaloop charts)',
      'Verify with the bundled health checks, then explore the APIs and the Testing Toolkit UI',
    ],
  },
  {
    key: 'preprod',
    title: 'Test & pre-production — Helm on Kubernetes',
    audience: 'Scheme operators standing up shared test environments',
    tool: 'Official Helm charts on a managed Kubernetes cluster',
    repo: 'https://github.com/mojaloop/helm',
    docs: 'https://docs.mojaloop.io/technical/deployment-guide/',
    summary:
      'The charts in mojaloop/helm deploy the full hub onto any conformant Kubernetes '
      + 'cluster. Right for shared QA/UAT environments and for schemes that already run '
      + 'their own cluster platform. Check each release’s notes for its supported '
      + 'Kubernetes versions before provisioning.',
    steps: [
      'Provision a Kubernetes cluster on the release’s supported version range (see the helm release notes)',
      'Install backend dependencies (Kafka, MySQL, Redis, MongoDB) — the example-mojaloop-backend chart bootstraps a non-production set',
      'helm repo add mojaloop https://mojaloop.io/helm/repo/ && helm install <release> mojaloop/mojaloop',
      'Run the Testing Toolkit Golden Path collections against the deployment to validate it',
    ],
  },
  {
    key: 'production',
    title: 'Production — Infrastructure-as-Code',
    audience: 'Scheme / hub operators going live',
    tool: 'mojaloop/iac-modules (Terraform + Ansible, ArgoCD “Control Center”)',
    repo: 'https://github.com/mojaloop/iac-modules',
    docs: 'https://docs.mojaloop.io/product/features/deployment/tools.html',
    summary:
      'The recommended production path is NOT raw Helm: Mojaloop’s IaC builds the '
      + 'entire estate — cloud-agnostic (including on-premises) Kubernetes, the hub itself, '
      + 'plus Vault, Keycloak, service mesh, monitoring, backup and DR — driven by Terraform '
      + 'and GitOps (ArgoCD) from a Control Center cluster. Start from the IaC templates and '
      + 'engage the Mojaloop community early; a national switch go-live is an infrastructure '
      + 'programme, not a chart install.',
    steps: [
      'Stand up the Control Center (Terraform) — the GitOps CI/CD cluster that manages every environment',
      'Define environments (dev/uat/prod) in the IaC configuration; Terraform + Ansible build the clusters and platform services',
      'ArgoCD deploys and continuously reconciles the Mojaloop hub from the versioned config',
      'Onboard DFSPs (scheme CA certs, endpoint registration, limits), then validate with TTK and scheme-level go-live rehearsals',
    ],
  },
];

export const SIZING_TIERS = [
  {
    tier: 'Lab (mini-loop)',
    shape: '1 VM — 8 vCPU / 16 GB RAM (8 GB min) / 60+ GB disk',
    note: 'Everything on one node via k3s. Fine for functional exploration only.',
  },
  {
    tier: 'Test / pre-production (Helm)',
    shape: '3× worker nodes — 4 vCPU / 16 GB RAM / 40 GB each, plus control plane',
    note: 'The documented starting point. Persistent-volume storage for Kafka/MySQL is additional.',
  },
  {
    tier: 'Production (IaC)',
    shape: 'HA control plane (3 masters + 3 etcd) and ≥3 workers of 4 vCPU / 16 GB as a floor',
    note: 'Treat as a floor, not a target: size from your TPS goals and run load tests before '
        + 'go-live. Kafka and MySQL move to HA topologies with dedicated fast storage; DR '
        + 'doubles the estate.',
  },
];

export const SECURITY_ITEMS = [
  {
    title: 'Mutual TLS everywhere DFSPs connect',
    body: 'Production hubs terminate DFSP traffic behind mTLS with certificates issued by the '
        + 'scheme CA. The sandbox’s pending ISO-SEC-001 case is exactly this control.',
  },
  {
    title: 'Message signing',
    body: 'Every FSPIOP message is JWS-signed; ISO 20022 flows follow the Mojaloop ISO market '
        + 'practice. The XMLDSig BAH profile you certified against here plays the same role: '
        + 'per-message integrity and non-repudiation bound to a registered participant key.',
  },
  {
    title: 'Key & certificate lifecycle',
    body: 'Scheme onboarding issues, rotates and revokes participant keys — the production '
        + 'counterpart of this sandbox’s Signing Certificates page. Budget for rotation '
        + 'drills, not just issuance.',
  },
  {
    title: 'Secrets, identity and segmentation',
    body: 'The IaC path bundles Vault (secrets/PKI) and Keycloak (operator IAM). Add Kubernetes '
        + 'network policies and keep DFSP-facing ingress separate from operator portals.',
  },
];

/**
 * The genuinely original content: how this sandbox maps onto the production hub.
 * sandbox → what you exercised here; production → the counterpart at a real switch.
 */
export const SANDBOX_TO_PRODUCTION = [
  {
    sandbox: 'POST /api/switch/iso20022/account/lookup — signed acmt.023 → acmt.024',
    production: 'account-lookup-service: party lookup (GET /parties → async PUT /parties callback; acmt.023/.024 under the ISO market practice)',
  },
  {
    sandbox: 'POST /api/switch/iso20022/clearing/credit — signed pacs.008 → pacs.002',
    production: 'ml-api-adapter + central-ledger: transfer prepare/fulfil (POST /transfers → async PUT /transfers callback; pacs.008/pacs.002 in ISO mode)',
  },
  {
    sandbox: 'POST /api/switch/iso20022/account/registration — acmt.007 → acmt.010/011 + acmt.014',
    production: 'account-lookup-service oracles + scheme onboarding: registering which accounts you serve (POST /participants; account-registration market practice)',
  },
  {
    sandbox: 'Recall choreography — switch-issued camt.056 to your endpoint, answered with signed camt.029',
    production: 'Payment recall per the Mojaloop ISO 20022 market practice (camt.056/camt.029), delivered over the hub’s callback channel',
  },
  {
    sandbox: 'Callback Endpoints page + outbound certification round (ISO-CB-00x)',
    production: 'Your DFSP callback endpoints registered with the hub. Production Mojaloop is callback-first: every response arrives asynchronously at your registered endpoint — the behaviour the outbound round certifies',
  },
  {
    sandbox: 'XMLDSig BAH signing profile with registered tenant certificates',
    production: 'JWS signing (FSPIOP) / ISO signing per market practice, with keys issued by the scheme CA over mTLS',
  },
  {
    sandbox: 'Certification suite, signed report and AfricaNenda sign-off',
    production: 'Testing Toolkit Golden Path collections and the scheme’s own conformance / go-live gate',
  },
];

export const VNEXT_NOTE =
  'Mojaloop vNext — a TypeScript re-architecture of the platform — reached beta in December '
  + '2023 but is not production-complete; the Foundation has since refocused its approach. '
  + 'Production deployments today use the classic v17.x Helm/IaC stack described here. '
  + 'Watch the roadmap, but do not plan a go-live on vNext.';

export const DISCLAIMER =
  'This page is AfricaNenda’s curated guide, verified against the release '
  + 'shown above on the date shown. It is not a substitute for the official Mojaloop '
  + 'documentation — always confirm versions, supported Kubernetes ranges and sizing against '
  + 'docs.mojaloop.io and the release notes before committing infrastructure.';
